import { MapBaseOverlay } from '../base-overlay';
import { IGoogleMapLayerEvents } from './layer-events';

export class GoogleMapLayer<
  TElement extends MapBaseOverlay = MapBaseOverlay,
  TEvents extends IGoogleMapLayerEvents = IGoogleMapLayerEvents,
> extends MapBaseOverlay<TEvents> {
  protected overlays = new Map<string, TElement>();

  constructor() {
    super();
  }

  public setZIndex(zIndex: number): this {
    super.setZIndex(zIndex);

    this.forEachOverlay((overlay) => {
      overlay.setZIndex(zIndex);
    });

    return this;
  }

  public setOpacity(opacity: number): this {
    super.setOpacity(opacity);

    this.forEachOverlay((overlay) => {
      overlay.setOpacity(opacity);
    });

    return this;
  }

  public getOverlay(id: string): TElement {
    return this.overlays.get(id);
  }

  public setMapOverlay(overlay: TElement): void {
    this.map.overlays.set(overlay.getId(), overlay);
  }

  public addOverlay(overlay: TElement): this {
    if (this.hasOverlay(overlay)) {
      this.removeOverlay(overlay);
    }
    this.overlays.set(overlay.getId(), overlay);

    if (this.map) {
      this.map.overlays.add(overlay);
    }

    overlay.setZIndex(this.zIndex);
    overlay.addEventedParent(this);

    this.fire('overlay:add');

    return this;
  }

  public removeOverlay(overlayIdOrInstance: TElement | string): this {
    if (this.hasOverlay(overlayIdOrInstance)) {
      const overlay: TElement =
        typeof overlayIdOrInstance === 'string'
          ? this.overlays.get(overlayIdOrInstance)
          : overlayIdOrInstance;

      this.overlays.delete(overlay.getId());
      if (this.map) {
        this.map.overlays.remove(overlay);
      }

      overlay.removeEventedParent(this);

      this.fire('overlay:remove');
    }

    return this;
  }

  public removeIdleOverlays(timeout: number, dateProperty?: string): any {
    const removedOverlays = [];
    this.forEachOverlay((element: any) => {
      const updatedTime = element?.feature?.properties[dateProperty]
        ? element?.feature?.properties[dateProperty]
        : element?.feature[dateProperty]
        ? element?.feature[dateProperty]
        : element.getUpdated();
      if (Date.now() - updatedTime >= timeout) {
        this.removeOverlay(element);
        removedOverlays.push(element);
      }
    });
    return removedOverlays;
  }

  public clearOverlays(): void {
    this.forEachOverlay(this.removeOverlay.bind(this));
  }

  public hasOverlay(overlayIdOrInstance: TElement | string): boolean {
    const overlayId: string =
      typeof overlayIdOrInstance === 'string' ? overlayIdOrInstance : overlayIdOrInstance.getId();

    return this.overlays.has(overlayId);
  }

  public forEachOverlay(callbackfn: (overlay: TElement) => void): this {
    this.overlays.forEach(callbackfn);
    return this;
  }

  protected draw(): void {}

  protected onAdd(): void {
    this.forEachOverlay((overlay) => overlay.addTo(this.map));
  }

  protected onRemove(): void {
    this.forEachOverlay((overlay) => overlay.remove());
  }
}
